import InputGroupContent from '@/components/elements/input'
import TextareaGroupContent from '@/components/elements/textarea'
import Icon from '@/components/shared/icons'
import {
	Box,
	Flex,
	Grid,
	Heading,
	Input,
	ListRoot,
	Text,
} from '@chakra-ui/react'
import Submit from '@/components/elements/submit'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ProductFormValues, productsSchema } from '@/entities/schemas/products'
import Select, { SelectOption } from '@/components/elements/select'
import { paymethodsMap } from '@/utils/maps/paymethods-map'
import { categoryOptions } from '@/utils/maps/categories-maps'
import Toast from '@/components/elements/toast'
import { activeMap } from '@/utils/maps/active-map'
import { Entities } from '@/entities/api/entities'
import { useUploader } from '@/hooks/useUploader'
import SpinnerLoading from '@/components/elements/spinner'
import { updateProductById } from '@/services/api/products/products'
import {
	productsRefrestUpdate,
	sessionRefrestUpdate,
} from '@/store/features/state'
import productDetailsConvert from '@/utils/product-details-convert'
import {
	updateProductFn,
	updateSelectProductFn,
} from '@/store/features/products'
import { useImageUploadToast } from '@/hooks/useImageUploadToast'
import useManualProgress from '@/hooks/useManualProgress'
import AttributeProductEntity, {
	AttributeProductSelect,
	ReferenceType,
	ValueType,
} from '@/entities/api/products/attributes'
import { attributesMap } from '@/utils/maps/attributes-map'
import { NameAttributes } from '@/entities/api/products/name-attributes'
import ProductEntity from '@/entities/api/products/products'
import {
	createAttribute,
	deleteAttributeById,
	updateAttributeById,
} from '@/services/api/products/attributes'
import ClothesForm from './parts/stock/clothes-form'
import PlansForm from './parts/stock/plans-form'
import AccessoriesForm from './parts/stock/accessories-form'
import SupplementsForm from './parts/stock/supplements-form'
import { API_URL } from '@/services/api/keys'
import ImageItem from './parts/stock/image-item'
import {
	createImagesProductById,
	removeImageProductById,
} from '@/services/api/products/images'
import AttributeItem from './parts/stock/attribute-item'
import { booleanMap } from '@/utils/maps/boolean-map'
import nameAttributeConvert from '@/utils/name-attributes-convert'
import { InputGroup } from '@/components/ui/input-group'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

const useClickOutside = (
	ref: React.RefObject<HTMLElement | null>,
	handler: () => void
) => {
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				handler()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [ref, handler])
}

const MultiSelectAttributes = ({
	options,
	selectedValues,
	onSelectionChange,
	reset,
	isDisabled = false,
}: {
	options: SelectOption[]
	selectedValues: SelectOption[]
	onSelectionChange: (values: SelectOption[]) => void
	reset?: boolean
	isDisabled?: boolean
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState('')
	const dropdownRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	useClickOutside(dropdownRef, () => setIsOpen(false))

	useEffect(() => {
		if (reset) {
			onSelectionChange([])
			setSearchTerm('')
			setIsOpen(false)
		}
	}, [reset, onSelectionChange])

	const toggleDropdown = () => {
		if (!isDisabled) {
			setIsOpen(!isOpen)
			if (!isOpen) {
				setSearchTerm('')
				setTimeout(() => {
					if (inputRef.current) {
						inputRef.current.focus()
					}
				}, 10)
			}
		}
	}

	const handleSelect = (option: SelectOption) => {
		const isSelected = selectedValues.some(
			(item) => item.value === option.value && item.type === option.type
		)

		if (isSelected) {
			onSelectionChange(
				selectedValues.filter(
					(item) => !(item.value === option.value && item.type === option.type)
				)
			)
		} else {
			const optionType = option.type as NameAttributes

			const hasSelectedType = selectedValues.some(
				(item) => item.type === optionType
			)

			if (hasSelectedType) {
				Toast({
					type: 'error',
					message: `Ya tienes seleccionado un atributo de tipo ${nameAttributeConvert(
						optionType
					)}. Solo puedes tener uno por conjunto de atributos.`,
				})
				return
			}

			onSelectionChange([...selectedValues, option])
		}
	}

	const removeSelected = (option: SelectOption) => {
		onSelectionChange(
			selectedValues.filter(
				(item) => !(item.value === option.value && item.type === option.type)
			)
		)
	}

	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	)

	return (
		<Box position='relative' width='full' mt={4} mb={4} ref={dropdownRef}>
			<Text mb={2} color='black'>
				Atributos del producto
				<Text as='span' color='red.500'>
					{' '}
					*
				</Text>
			</Text>

			{selectedValues.length > 0 && (
				<Box mb={2}>
					<Flex wrap='wrap' gap={2}>
						{selectedValues.map((item, index) => (
							<Box
								key={`${item.value}-${item.type}-${index}`}
								bg='blue.100'
								color='blue.800'
								px={3}
								py={1}
								borderRadius='full'
								fontSize='sm'
								display='flex'
								alignItems='center'
								gap={2}
							>
								<Text>
									{item.type
										? `${nameAttributeConvert(item.type as NameAttributes)} - ${
												item.label
										  }`
										: item.label}
								</Text>
								<Box
									cursor='pointer'
									onClick={() => removeSelected(item)}
									_hover={{ color: 'blue.600' }}
								>
									<Icon icon='close' size='sm' />
								</Box>
							</Box>
						))}
					</Flex>
				</Box>
			)}

			<Flex
				onClick={toggleDropdown}
				cursor={isDisabled ? 'not-allowed' : 'pointer'}
				p={3}
				borderWidth={1}
				borderColor='gray.200'
				borderRadius='md'
				bg={isDisabled ? 'gray.100' : 'white'}
				color={isDisabled ? 'gray.500' : 'black'}
				justifyContent='space-between'
				alignItems='center'
				opacity={isDisabled ? 0.6 : 1}
				_hover={!isDisabled ? { borderColor: 'gray.300' } : {}}
				_focus={
					!isDisabled
						? { borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }
						: {}
				}
			>
				<Text overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
					{selectedValues.length > 0
						? `${selectedValues.length} atributo(s) seleccionado(s)`
						: 'Selecciona los atributos del producto'}
				</Text>
				<Icon icon='chevronDown' size='lg' color='black' />
			</Flex>

			{isOpen && (
				<Box
					position='absolute'
					width='full'
					mt={2}
					bg='white'
					borderWidth={1}
					borderColor='gray.200'
					borderRadius='md'
					boxShadow='lg'
					zIndex={10}
					maxHeight='300px'
					display='flex'
					flexDirection='column'
				>
					<Box p={3} borderBottomWidth={1} borderColor='gray.200'>
						<InputGroup
							w='full'
							startElement={
								<Box ml='2'>
									<Icon icon='search' size='lg' color='gray.400' />
								</Box>
							}
						>
							<Input
								ref={inputRef}
								placeholder='Buscar atributos...'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onClick={(e) => e.stopPropagation()}
								_focus={{ borderColor: 'blue.500' }}
							/>
						</InputGroup>
					</Box>

					<Box borderBottomWidth='1px' borderColor='gray.200' my={0} />

					<Box overflowY='auto' maxHeight='232px'>
						{filteredOptions.length > 0 ? (
							<Box as='ul' listStyleType='none' m={0} p={0}>
								{filteredOptions.map((option, index) => {
									const isSelected = selectedValues.some(
										(item) =>
											item.value === option.value && item.type === option.type
									)

									const optionType = option.type as NameAttributes
									const hasSelectedType = selectedValues.some(
										(item) => item.type === optionType
									)
									const isDisabledOption = hasSelectedType

									return (
										<Box
											as='li'
											key={`${option.value}-${option.type}-${index}`}
											onClick={() => !isDisabledOption && handleSelect(option)}
											p={4}
											py={2}
											_hover={!isDisabledOption ? { bg: 'gray.100' } : {}}
											cursor={isDisabledOption ? 'not-allowed' : 'pointer'}
											bg={
												isSelected
													? 'blue.50'
													: isDisabledOption
													? 'gray.50'
													: ''
											}
											fontWeight={isSelected ? 'bold' : 'normal'}
											opacity={isDisabledOption ? 0.5 : 1}
											display='flex'
											alignItems='center'
											justifyContent='space-between'
										>
											<Text>
												{option.type
													? `${nameAttributeConvert(
															option?.type as NameAttributes
													  )} - ${option.label}`
													: option.label}
											</Text>
											{isSelected && (
												<Icon icon='check' size='sm' color='blue.500' />
											)}
											{isDisabledOption && !isSelected && (
												<Icon icon='lock' size='sm' color='gray.400' />
											)}
										</Box>
									)
								})}
							</Box>
						) : (
							<Box p={4} textAlign='center' color='gray.500'>
								No hay opciones disponibles
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	)
}

const EditStock = ({ close }: { close: () => void }) => {
	const { selectProduct: product } = useSelector(
		(state: RootState) => state.products
	)
	if (product === null) return
	const token = useSelector((state: RootState) => state.auth.token)
	const categories = useSelector(
		(state: RootState) => state.categories.categories
	)
	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<ProductFormValues>({ resolver: yupResolver(productsSchema) })
	useFormErrorToast(errors)
	const {
		inputRef,
		files,
		isProcessing,
		handleFileChange,
		clearFiles,
		removeFile,
	} = useUploader()
	const {
		inputRef: attributesInputRef,
		files: attributesFiles,
		isProcessing: attributesIsProcessing,
		handleFileChange: attributesHandleFileChange,
		clearFiles: attributesClearFiles,
	} = useUploader()
	const [entitySelect, setEntitySelect] = useState<SelectOption | null>({
		label:
			categoryOptions.find((obj) => obj.value === product.type)?.label ?? '',
		value: product.type,
	})
	const [isHaveShipping, setIsHaveShipping] = useState<SelectOption | null>({
		label: product?.shipping_info.isHaveShipping ? 'Si' : 'No',
		value: product?.shipping_info.isHaveShipping,
	})
	const [categorySelect, setCategorySelect] = useState<SelectOption | null>({
		label: product.category.name,
		value: product.category._id,
	})
	const [attributes, setAttributes] = useState<AttributeProductSelect[] | []>(
		[]
	)
	const [attributeLocal, setAttributeLocal] =
		useState<AttributeProductSelect | null>(null)
	const [attribute, setAttribute] = useState<AttributeProductEntity | null>()
	const [selectedAttributes, setSelectedAttributes] = useState<SelectOption[]>(
		[]
	)
	const [isAddAttribute, setIsAddAttribute] = useState<boolean>(false)
	const [isEditAttribute, setIsEditAttribute] = useState<boolean>(false)
	const [selectPrice, setSelectPrice] = useState<number>(0)
	const [selectStock, setSelectStock] = useState<number>(0)
	const [reset, setReset] = useState<boolean>(false)
	const dispatch = useDispatch<AppDispatch>()
	const [isUploadImage, setIsUploadImage] = useState<boolean>(false)
	const { handleProgress } = useImageUploadToast()
	const { progress, isLoading, startProgress, completeProgress, failProgress } =
		useManualProgress()

	const categoriesDataMap: SelectOption[] = useMemo(() => {
		if (!categories || categories.length === 0) return []
		return categories
			.filter(
				(category) =>
					category?.type === entitySelect?.value &&
					typeof category._id === 'string'
			)
			.map((category) => ({
				label: category.name,
				value: category._id as string,
			}))
	}, [categories, entitySelect])

	const onSubmit = async (dataYup: ProductFormValues) => {
		try {
			startProgress()

			if (product?.images && files.length + product?.images?.length >= 5) {
				Toast({
					type: 'error',
					message: 'No puedes agregar mas de 5 imagenes',
				})
				await failProgress()
				return
			}

			const { details, category, shipping_info, discount, ...restData } =
				dataYup
				console.log(details)
			const detailsData = productDetailsConvert(details, restData.type)
			const images: File[] = files.map((file) => file.file)
			const productPayload = {
				...restData,
				details: { ...detailsData },
				category: {
					_id: (categorySelect?.value as string) ?? '',
					name: (categorySelect?.label as string) ?? '',
				},
				shipping_info: {
					isHaveShipping: isHaveShipping?.value as boolean,
					type: shipping_info.type!,
					price: shipping_info.price ? shipping_info.price : 0,
				},
				discount: discount ? discount : 0,
			}

			const [productResponse, imagesResponse] = await Promise.all([
				await updateProductById({
					id: product._id ?? '',
					token: token ?? '',
					product: productPayload,
				}),
				await createImagesProductById(token ?? '', product._id ?? '', images),
			])

			if (
				[500, 409, 400].includes(
					imagesResponse?.status || productResponse?.status || 0
				)
			) {
				Toast({ type: 'error', message: 'No se pudo crear las imagenes' })
				await failProgress()
				return
			}

			if (imagesResponse?.status === 413) {
				Toast({ type: 'error', message: 'El archivo es muy grande para ser procesado por el api' })
				await failProgress()
				return
			}


			if ((imagesResponse?.status || productResponse?.status || 0) == 401) {
				dispatch(sessionRefrestUpdate())
				await failProgress()
				return
			}

			const responseData: ProductEntity = productResponse?.data?.data ?? {}

			await Promise.all(
				attributes.map(async (item) => {
					const response = await createAttribute(
						token ?? '',
						product._id ?? '',
						{
							name: item.name,
							type: item.type,
							value: item.value,
							reference: item.reference,
							price: item.price,
							quantity: item.quantity,
						},
						item.image
					)

					if ([500, 409, 400].includes(response?.status || 0)) {
						Toast({ type: 'error', message: 'No se pudo crear el atributo' })
						await failProgress()
						return
					}

					if (response?.status === 413) {
						Toast({ type: 'error', message: 'El archivo es muy grande para ser procesado por el api' })
						await failProgress()
						return
					}

					if (response?.status === 401) {
						dispatch(sessionRefrestUpdate())
						await failProgress()
						return
					}

					responseData.attributes = [response?.data?.data]
				})
			)

			const newProduct = {
				...responseData,
				images: [
					...(product?.images ?? []),
					...(imagesResponse?.data?.data ?? []),
				],
			}

			dispatch(updateProductFn(newProduct), updateSelectProductFn(newProduct))
			dispatch(productsRefrestUpdate())
			await completeProgress()

			close()
			clearFiles()
		} catch (error) {
			Toast({ type: 'error', message: 'Algo salió mal al crear el producto' })
			await failProgress()
		}
	}

	const addAttribute = () => {
		if (selectedAttributes.length === 0) {
			Toast({
				type: 'error',
				message: 'Debes seleccionar al menos un atributo',
			})
			return
		}

		if (attributesFiles.length === 0) {
			Toast({
				type: 'error',
				message: 'Debes agregar una imagen para el atributo',
			})
			return
		}

		if (selectPrice <= 0) {
			Toast({
				type: 'error',
				message: 'El precio debe ser mayor a 0',
			})
			return
		}

		if (selectStock <= 0) {
			Toast({
				type: 'error',
				message: 'La cantidad debe ser mayor a 0',
			})
			return
		}

		const names: NameAttributes[] = []
		const values: ValueType[] = []
		const references: ReferenceType[] = []

		selectedAttributes.forEach((attr) => {
			names.push(attr.type as NameAttributes)
			values.push(attr.label as ValueType)
			references.push(attr.value as ReferenceType)
		})

		const attributeData: AttributeProductSelect = {
			name: names,
			type: entitySelect?.value as Entities,
			value: values,
			reference: references,
			price: selectPrice,
			quantity: selectStock,
			image: attributesFiles[0].file,
			imageId: attributesFiles[0].id,
			imagePreview: attributesFiles[0].preview || '',
		}

		setAttributes((prev) => [...prev, attributeData])
		setSelectedAttributes([])
		attributesClearFiles()
		setSelectPrice(0)
		setSelectStock(0)
		setIsAddAttribute(false)
	}

	const deleteAttribute = (id: string) =>
		setAttributes((prev) => prev.filter((item) => item.imageId !== id))

	const deleteFile = async (id: string) => {
		try {
			if (product?.images?.length == 1) {
				Toast({
					type: 'error',
					message: 'No puedes eliminar la ultima imagen',
				})
				return
			}

			await removeImageProductById(token ?? '', product._id ?? '', id)
			const newImages =
				product?.images && product?.images?.filter((item) => item._id !== id)

			const updatedData = { _id: product._id, images: newImages }
			dispatch(updateProductFn(updatedData))
			dispatch(updateSelectProductFn(updatedData))
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo eliminar la imagen' })
		}
	}

	const deleteAttributeByApi = async (id: string) => {
		try {
			await deleteAttributeById(token ?? '', product._id ?? '', id)
			const newAttributes = product?.attributes?.filter(
				(item) => item._id !== id
			)
			const updatedData = { _id: product._id, attributes: newAttributes }
			dispatch(updateProductFn(updatedData))
			dispatch(updateSelectProductFn(updatedData))
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo eliminar el attributo' })
		}
	}

	const updateAttributeByApi = async (id: string) => {
		try {
			await updateAttributeById(token ?? '', product._id ?? '', id, {
				quantity: selectStock,
				price: selectPrice,
			})

			const index = product?.attributes?.findIndex((attr) => attr._id === id)

			if (index === undefined || index === -1) {
				throw new Error('Atributo no encontrado')
			}

			const updatedAttributes = [...(product?.attributes ?? [])]

			updatedAttributes[index] = {
				...updatedAttributes[index],
				quantity: selectStock,
				price: selectPrice,
			}

			const updatedProduct = {
				...product,
				attributes: updatedAttributes,
			}

			dispatch(updateProductFn(updatedProduct))
			dispatch(updateSelectProductFn(updatedProduct))
			setSelectPrice(0)
			setSelectStock(0)
			setIsEditAttribute(false)
			setAttributeLocal(null)
			setAttribute(null)
		} catch (error) {
			console.log(error)
			Toast({ type: 'error', message: 'No se pudo actualizar el attribuo' })
		}
	}

	const updateAttribute = (id: string) => {
		const updatedAttributes = attributes.map((item) =>
			item.imageId === id
				? { ...item, price: selectPrice, quantity: selectStock }
				: item
		)

		setAttributes(updatedAttributes)
		setIsEditAttribute(false)
		setSelectPrice(0)
		setSelectStock(0)
		setAttribute(null)
		setAttributeLocal(null)
	}

	useEffect(() => {
		setSelectPrice((attributeLocal ? attributeLocal : attribute)?.price || 0)
		setSelectStock((attributeLocal ? attributeLocal : attribute)?.quantity || 0)
	}, [attribute, attributeLocal])

	useEffect(() => {
		setAttributes([])
		setSelectedAttributes([])
	}, [entitySelect])

	useEffect(() => {
		if (files.length > 0) {
			setIsUploadImage(true)
			Toast({
				type: 'success',
				duration: 1000,
				message: 'La imagen se cargo correctamente',
			})
		}
		if (files.length >= 5) {
			Toast({
				type: 'info',
				duration: 1000,
				message: 'Solo puedes agregar 5 images por producto',
			})
		}
	}, [files])

	useEffect(() => {
		if (progress !== undefined && isLoading !== undefined) {
			handleProgress(progress, isLoading)
		}
	}, [progress, isLoading])

	useEffect(() => {
		if (attributes.length > 0) {
			if (reset) {
				const timer = setTimeout(() => {
					setReset(false)
					setTimeout(() => {
						setReset(true)
					}, 100)
				}, 100)
				return () => clearTimeout(timer)
			} else {
				setReset(true)
			}
		}
	}, [attributes.length])

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex w='100%' h='full' flexDirection='column' alignItems='center'>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='2'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Informacion general
					</Heading>
				</Box>
				<InputGroupContent
					label='Nombre del producto'
					placeholder='Ingresa el nombre del producto'
					name='name'
					required
					type='text'
					defaultValue={product.name}
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Marca del producto'
					placeholder='Ingresa la marca del producto'
					name='brand'
					required
					type='text'
					defaultValue={product.brand}
					register={register}
					errors={errors}
				/>
				<TextareaGroupContent
					label='Descripcion del producto'
					placeholder='Ingresa la descripcion del producto'
					name='description'
					required
					type='text'
					defaultValue={product?.description || 'sin descripcion'}
					register={register}
					errors={errors}
				/>
				<InputGroupContent
					label='Descuento en % (Opcional)'
					placeholder='Ingresa el descuento del producto en caso de tener alguno'
					name='discount'
					type='number'
					defaultValue={product.discount || 0}
					register={register}
					errors={errors}
				/>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='4'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Imagenes (Max 5)
					</Heading>
				</Box>
				<Box w='full' mb='4'>
					<Grid
						w='full'
						border='0.1px solid gray'
						borderRadius='lg'
						p='4'
						gap='4'
						templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }}
					>
						{product?.images &&
							product.images.length > 0 &&
							product.images.map((item, index) => (
								<ImageItem
									key={index}
									id={item._id ?? ''}
									name={`image product - ${index}`}
									preview={`${API_URL}/${item.url}`}
									removeFile={deleteFile}
								/>
							))}

						{files &&
							isUploadImage &&
							files.map((item, index) => (
								<ImageItem
									key={index}
									id={item.id}
									name={item.name}
									preview={item.preview ?? ''}
									removeFile={removeFile}
								/>
							))}

						<Box position='relative' h='12vh'>
							{isProcessing ? (
								<SpinnerLoading />
							) : (
								<>
									<Input
										type='file'
										accept='image/*'
										multiple
										ref={inputRef}
										onChange={handleFileChange}
										opacity='0'
										position='absolute'
										h='full'
										w='full'
										disabled={files.length >= 5}
										cursor='pointer'
										zIndex='2'
									/>
									<Box
										h='full'
										w='full'
										border='2px dashed gray'
										display='flex'
										alignItems='center'
										justifyContent='center'
										borderRadius='md'
										position='relative'
										zIndex='1'
										p='2'
										bg='gray.100'
										_hover={{ bg: 'gray.200' }}
									>
										<Icon
											icon={files.length >= 5 ? 'disabled' : 'camera'}
											size='xl'
											color='gray'
										/>
										<Text
											ml='2'
											color='gray.500'
											fontSize='sm'
											display={{ base: 'none', md: 'block' }}
										>
											{files.length >= 5 ? 'Candidad Max' : 'Añadir Imagen'}
										</Text>
									</Box>
								</>
							)}
						</Box>
					</Grid>
				</Box>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='4'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Selecionables
					</Heading>
				</Box>
				<Select
					name='type'
					label='Tipo del producto'
					selectValue={setEntitySelect}
					placeholder='Selecciona el tipo o entidad del producto'
					defaultValue={{
						label:
							categoryOptions.find((obj) => obj.type === product.type)?.label ??
							'',
						value: product.type,
					}}
					options={categoryOptions}
					control={control}
					isRequired={true}
				/>
				{entitySelect && (
					<Select
						control={control}
						name='category'
						label='Categoria del producto'
						placeholder='Selecciona la categoria del producto'
						options={categoriesDataMap}
						selectValue={setCategorySelect}
						defaultValue={{
							label: product.category.name,
							value: product.category._id,
						}}
						isRequired={true}
					/>
				)}
				<Select
					name='active'
					label='Visibilidad del producto en la tienda'
					placeholder='Selecciona la visibilidad del producto.'
					options={activeMap}
					control={control}
					defaultValue={{
						label:
							activeMap.find((obj) => obj.value === product.active)?.label ??
							'',
						value: product.active,
					}}
					isRequired={true}
				/>
				{entitySelect && (
					<>
						<Box
							w='full'
							display='flex'
							justifyContent='start'
							alignItems='center'
						>
							<Heading
								as='h4'
								fontSize={{ base: 'lg', md: 'xl' }}
								color='black'
								fontWeight='900'
							>
								Detalles del producto
							</Heading>
						</Box>
						{entitySelect?.value === Entities.CLOTHES && (
							<ClothesForm
								control={control}
								product={product}
								errors={errors}
								register={register}
							/>
						)}
						{entitySelect?.value === Entities.PLANS_PERSONALIZED && (
							<PlansForm control={control} product={product} />
						)}
						{entitySelect?.value === Entities.ACCESSORIES && (
							<AccessoriesForm
								control={control}
								errors={errors}
								product={product}
								register={register}
							/>
						)}
						{entitySelect?.value === Entities.SUPPLEMENTS && (
							<SupplementsForm
								control={control}
								errors={errors}
								product={product}
								register={register}
							/>
						)}
						<Box
							w='full'
							display='flex'
							justifyContent='start'
							alignItems='center'
							mb='4'
						>
							<Heading
								as='h4'
								fontSize={{ base: 'lg', md: 'xl' }}
								color='black'
								fontWeight='900'
							>
								Atributos
							</Heading>
						</Box>
						<Box w='full' mb='4'>
							<ListRoot w='full' gap='2'>
								{attributes.length > 0 &&
									attributes.map((item, index) => (
										<AttributeItem
											key={index}
											url={item.imagePreview}
											attribute={{
												name: item.name,
												reference: item.reference,
												price: item.price,
												quantity: item.quantity,
												value: item.value,
												_id: item.imageId,
											}}
											onEdit={() => {
												setAttributeLocal(item)
												setIsEditAttribute(true)
											}}
											deleteAttribute={deleteAttribute}
										/>
									))}
								{product?.attributes &&
									product.attributes?.length > 0 &&
									product?.attributes.map((attribute, index) => (
										<AttributeItem
											key={index}
											url={`${API_URL}/${attribute.image?.url}`}
											attribute={{
												name: attribute.name,
												reference: attribute.reference,
												price: attribute.price,
												quantity: attribute.quantity,
												value: attribute.value,
												_id: attribute._id,
											}}
											deleteAttribute={deleteAttributeByApi}
											onEdit={() => {
												setAttribute(attribute)
												setIsEditAttribute(true)
											}}
										/>
									))}
							</ListRoot>
							<Box ml='4' mb='4' mt='4'>
								{isEditAttribute && (
									<>
										<InputGroupContent
											label='Cantidad del producto'
											type='number'
											defaultValue={
												(attributeLocal ? attributeLocal : attribute)?.quantity
											}
											selectValue={setSelectStock}
											required
										/>
										<InputGroupContent
											label='Precio del producto'
											required
											isPrice={true}
											defaultValue={
												(attributeLocal ? attributeLocal : attribute)?.price
											}
											change={(e: React.ChangeEvent<HTMLInputElement>) => {
												setSelectPrice(Number(e.target.value) || 0)
											}}
										/>
										<Flex>
											<Submit
												text='Editar'
												type='button'
												click={() =>
													attributeLocal
														? updateAttribute(attributeLocal.imageId)
														: updateAttributeByApi(attribute?._id || '')
												}
											/>
											<Box ml='4'>
												<Submit
													text='Cancelar'
													type='button'
													click={() => {
														setIsEditAttribute(false)
														setSelectPrice(0)
														setSelectStock(0)
														setAttribute(null)
														setAttributeLocal(null)
													}}
												/>
											</Box>
										</Flex>
									</>
								)}

								{!isEditAttribute && isAddAttribute && (
									<>
										<MultiSelectAttributes
											options={attributesMap(
												(entitySelect?.value as Entities) ?? Entities.ALL
											)}
											selectedValues={selectedAttributes}
											onSelectionChange={setSelectedAttributes}
											reset={reset}
										/>
										<InputGroupContent
											label='Cantidad del producto'
											type='number'
											defaultValue={0}
											reset={reset}
											selectValue={setSelectStock}
											required
										/>
										<InputGroupContent
											label='Precio del producto'
											isPrice={true}
											reset={reset}
											required
											change={(e: React.ChangeEvent<HTMLInputElement>) => {
												setSelectPrice(Number(e.target.value) || 0)
											}}
										/>
										<InputGroupContent
											label='Imagen del atributo'
											type='file'
											inputRef={attributesInputRef}
											disabled={attributesFiles.length > 0}
											change={attributesHandleFileChange}
											required
										/>
									</>
								)}

								{attributesIsProcessing ? (
									<SpinnerLoading />
								) : (
									!isEditAttribute && (
										<Submit
											text={
												isAddAttribute
													? 'Confirmar Atributo'
													: 'Agregar Atributo'
											}
											type='button'
											click={() => {
												if (isAddAttribute) {
													addAttribute()
													return
												}
												setIsAddAttribute(true)
											}}
										/>
									)
								)}
							</Box>
						</Box>
					</>
				)}

				<Box w='full' display='flex' justifyContent='start' alignItems='center'>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Costos de envio
					</Heading>
				</Box>

				<Select
					name='shipping_info.isHaveShipping'
					label='Seleciona si el producto tiene envio'
					placeholder='Selecciona si el producto tiene envio'
					selectValue={setIsHaveShipping}
					defaultValue={{
						label: product?.shipping_info.isHaveShipping ? 'Si' : 'No',
						value: product?.shipping_info.isHaveShipping,
					}}
					options={booleanMap}
					control={control}
					isRequired={true}
				/>

				{isHaveShipping?.value && (
					<>
						<Select
							name='shipping_info.type'
							label='Metodo de pago del envio'
							placeholder='Selecciona el metodo del pago'
							defaultValue={{
								label:
									paymethodsMap.find(
										(obj) => obj.value === product.shipping_info.type
									)?.label ?? '',
								value: product.shipping_info.type,
							}}
							options={paymethodsMap}
							control={control}
							isRequired={true}
						/>
						<InputGroupContent
							label='Costo de envio (Opcional)'
							placeholder='Ingresa el costo del envio en caso de pago'
							name='shipping_info.price'
							type='number'
							isPrice
							defaultValue={product?.shipping_info?.price || 0}
							register={register}
							errors={errors}
						/>
					</>
				)}
				<Box pt='4' pb='8'>
					{isLoading ? <SpinnerLoading /> : <Submit text='Guardar' />}
				</Box>
			</Flex>
		</form>
	)
}

export default EditStock
